import { type FormEvent, type ReactElement, useState } from 'react';
import { AdminLayout } from '../layout.tsx';
import { Button, Card, Input } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

const MULTIPLIERS = ['1.0', '1.25', '1.5', '2.0'] as const;

export function ExtendedTimeGrantPage(): ReactElement {
  const [userId, setUserId] = useState('');
  const [multiplier, setMultiplier] = useState<(typeof MULTIPLIERS)[number]>('1.0');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!MULTIPLIERS.includes(multiplier)) {
      setMessage('Invalid multiplier');
      return;
    }

    const targetUserId = userId.trim();
    const res = await apiFetch(`/api/admin/users/${targetUserId}/extended-time`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ multiplier: Number(multiplier) }),
    });
    const body = (await res.json()) as
      | { ok: true; data: { multiplier: number } }
      | { ok: false; error?: { message?: string } };

    if (!body.ok) {
      setMessage(body.error?.message ?? 'Grant failed');
      return;
    }

    setMessage(`Granted multiplier ${String(body.data.multiplier)} to ${targetUserId}`);
  }

  return (
    <AdminLayout title="Extended-Time Grant" eyebrow="Admin · Users">
      <Card variant="default" className="mt-6 max-w-lg">
        <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-5">
          <div>
            <label
              htmlFor="ext-user-id"
              className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
            >
              User ID
            </label>
            <Input
              id="ext-user-id"
              placeholder="Paste user UUID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </div>

          <div>
            <label
              htmlFor="ext-multiplier"
              className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
            >
              Multiplier
            </label>
            <select
              id="ext-multiplier"
              value={multiplier}
              onChange={(e) => setMultiplier(e.target.value as (typeof MULTIPLIERS)[number])}
              className="w-full rounded-md border border-line-2 bg-card px-3 py-2.5 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            >
              {MULTIPLIERS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="ext-reason"
              className="mb-1 block font-mono text-eyebrow font-medium uppercase tracking-wide text-muted"
            >
              Reason
            </label>
            <textarea
              id="ext-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-line-2 bg-card px-3 py-2 font-sans text-body text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent-l"
            />
          </div>

          <div>
            <Button variant="primary" type="submit">
              Grant extended time
            </Button>
          </div>
        </form>

        {message ? (
          <p className="mt-4 font-sans text-small text-ink-2" role="status">
            {message}
          </p>
        ) : null}
      </Card>
    </AdminLayout>
  );
}
